/**
 * CAOS-1904 — the one way into an impersonation, and the two doors that use it.
 *
 * ## Why this is its own module
 *
 * The overlay, the required reason, the POST and the reload were written for CAOS-607 inside
 * `shell-frame.ts`, reachable from exactly one place: the account menu. CAOS-1904 adds a second
 * place — a person's own page in Setup — and the ticket is explicit that this must be a second
 * DOOR and not a second implementation: "two paths minting sessions by two routes is the shape
 * `field-security-chooser.js` refused for field grants."
 *
 * So it moved here rather than being called across. `roster-detail-page.ts` importing
 * `shell-frame.ts` would close a cycle — `shell-frame` imports `content-source`, which reaches the
 * roster surfaces — and would pull four thousand lines of shell into every test that renders a
 * detail page. This module imports nothing from either caller, which is what makes it safe for
 * both to import it.
 *
 * ## The seams, and why they are owned here but set from there
 *
 * `shell-frame.ts`'s `setControlPlaneFetch`/`setLeaveTo` forward to the setters below, so there is
 * still ONE injection point for a test and production wiring that already calls them keeps
 * working. That follows the reason `getControlPlaneFetch` already gives for existing in that file:
 * sharing the seam, rather than each caller reaching for the bare global `fetch`, is what keeps the
 * same-origin credential handling and the test override working for every reader of a
 * control-plane route.
 *
 * ## What this module deliberately does not decide
 *
 * Whether to offer the control, and whether it is pressable. That is the page's to declare
 * (`enabledWhen`, and CAOS-1903's `reason` inside it) and the renderer's to apply, because only a
 * page knows it is about a person — see the `impersonate` kind's own note in
 * `kernel/src/meta/metatypes.ts` for why keying this off `impersonation/candidates` cannot work.
 */

/** A minimal response shape — only what this module reads. */
export interface DoorResponse {
  json(): Promise<unknown>;
}
export type DoorFetch = (path: string, init?: unknown) => Promise<DoorResponse>;
export type DoorLeaveTo = (path: string) => void;

/** CAOS-607 — begin acting as somebody else. The host lifts the returned token into its own cookie. */
export const IMPERSONATION_START_ENDPOINT = '/api/impersonation/start';

const defaultDoorFetch: DoorFetch = (path, init) =>
  fetch(path, { credentials: 'same-origin', ...((init as RequestInit | undefined) ?? {}) }) as unknown as Promise<DoorResponse>;
let doorFetch: DoorFetch = defaultDoorFetch;
let doorLeaveTo: DoorLeaveTo = (path) => window.location.assign(path);

/** Override the control-plane fetch this door posts through (production wiring / tests). */
export function setImpersonationFetch(fn: DoorFetch): void {
  doorFetch = fn;
}
/** Override the full-navigation seam this door leaves by (tests). */
export function setImpersonationLeaveTo(fn: DoorLeaveTo): void {
  doorLeaveTo = fn;
}

/**
 * CAOS-1904 AC2 — the one reason a caller cannot be told by the page it is on.
 *
 * A review found that only ONE of AC2's three cases was drawn-and-unavailable. The inactive target
 * is, through `enabledWhen` keyed on `subject.status`, because the page knows it. The other two
 * are facts about the SESSION rather than about the record, and a page cannot declare them: no
 * `valueAt` into a record payload can say "you are already acting as somebody". So the button was
 * pressable, a reason had to be typed, and only the submitted refusal said it was never going to
 * work — which is a different experience from the one AC2 asks for.
 *
 * This is the half the host can close. {@link setImpersonationRunning} is called by the frame,
 * where `runtime.impersonation` is in scope, and the renderer asks
 * {@link impersonationUnavailableReason} before it draws.
 *
 * WHAT IS STILL NOT CLOSED, AND WHY, because it is a real gap and not an oversight: "it is
 * yourself". Nothing the host holds is the signed-in person's PRINCIPAL ID. `runtime.user` carries
 * a display `name` and a `sub` LINE — a caption, which derives to "" and is then omitted — and
 * `SessionIdentity` in `metadata-source.ts` carries an identifier, roles, administered apps and
 * held permissions, and no principal id at all. So the addressed record cannot be compared with
 * the viewer without the kernel's whoami answering with one, which is kernel work rather than this
 * ticket's. That case stays drawn-and-refused, in the kernel's own words. AC2 sanctions exactly
 * this — "deliver the control with the best the vocabulary allows and say on this ticket which
 * half is waiting" — and it is said there and on the pull request rather than only here.
 */
export const IMPERSONATION_ALREADY_RUNNING =
  'End the session you are already acting in before starting another.';

let sessionRunning = false;

/** Tell this module whether a borrowed session is in force. Called by the frame, per render. */
export function setImpersonationRunning(running: boolean): void {
  sessionRunning = running;
}

/**
 * Why a door must not be opened right now, in a sentence, or null when it may be.
 *
 * Deliberately NOT a boolean: AC2's requirement is that the control says WHICH reason applies, so
 * the thing that knows the reason returns the sentence rather than leaving the renderer to invent
 * one from a flag.
 */
export function impersonationUnavailableReason(): string | null {
  return sessionRunning ? IMPERSONATION_ALREADY_RUNNING : null;
}

/**
 * What a door asks for. Everything optional is wording, and absent means this module's own default:
 * a page may decline to have an opinion about how the question is phrased.
 */
export interface ImpersonationAsk {
  /**
   * What to POST, minus the reason. The caller resolves this — the account menu builds
   * `{ principal: <id> }` from the picker, and an authored action builds it from its own `params`
   * through the same `resolveParams` every other action kind uses, so a page that needs to send the
   * target under a value the address cannot carry already has `paramsFrom` for it.
   *
   * `reason` is added here and is NOT read from this map: the route requires exactly that name, and
   * a caller able to rename it could author a control that cannot work.
   */
  readonly body: Readonly<Record<string, unknown>>;
  /** The person's name, for the heading — never an id if a label is to hand. */
  readonly label: string;
  /** Overrides the question above the box. */
  readonly reasonLabel?: string;
  /** Overrides the placeholder inside it. */
  readonly reasonHint?: string;
  /**
   * CAOS-1904 — offer a write-enabled session as an explicit, unticked second choice.
   *
   * Absent ⇒ read-only, and nothing is drawn. `users.impersonate` and `users.impersonate_write` are
   * two separate permissions, so a control that sent the flag without naming it would hand out the
   * stronger of them silently. Unticked by default for the same reason: the person has to choose it.
   */
  readonly offerWrite?: { readonly param: string; readonly label: string };
}

/**
 * CAOS-607/CAOS-1904 — ask why, then begin.
 *
 * The reason is REQUIRED at the moment an impersonation starts and is carried into the audit entry
 * against everything done in the session. That is why it is collected here, in the platform's own
 * overlay, rather than with `window.prompt`: a browser prompt cannot be styled, cannot say what the
 * reason is for, and is blocked outright in some embedding contexts — which would leave the one
 * required field silently uncollectable and the feature unusable with no visible cause.
 *
 * The kernel checks the reason again on arrival. This is a form, not a gate.
 */
export function askWhyThenStart(ask: ImpersonationAsk): void {
  const overlay = document.createElement('caos-overlay');
  overlay.setAttribute('open', '');
  overlay.setAttribute('heading', `Act as ${ask.label}`);

  const body = document.createElement('div');
  body.className = 'caos-impersonate-ask';

  const explain = document.createElement('p');
  explain.textContent =
    'You will see the workspace as this person sees it, limited to what you can already reach yourself. ' +
    'Everything you do is recorded against your own name, with theirs beside it.';
  body.append(explain);

  const label_ = document.createElement('label');
  label_.textContent = ask.reasonLabel ?? 'Why are you doing this?';
  const reason = document.createElement('textarea');
  reason.setAttribute('rows', '2');
  reason.placeholder = ask.reasonHint ?? 'A support ticket number, and what you are checking';
  label_.append(reason);
  body.append(label_);

  // CAOS-1904 — the stronger choice, drawn only when a page offered it, and never pre-ticked.
  let writeBox: HTMLInputElement | undefined;
  if (ask.offerWrite) {
    const wrap = document.createElement('label');
    wrap.className = 'caos-impersonate-ask__write';
    writeBox = document.createElement('input');
    writeBox.type = 'checkbox';
    wrap.append(writeBox, document.createTextNode(` ${ask.offerWrite.label}`));
    body.append(wrap);
  }

  const problem = document.createElement('p');
  problem.className = 'caos-impersonate-ask__problem';
  problem.setAttribute('role', 'alert');
  problem.hidden = true;
  body.append(problem);

  const actions = document.createElement('div');
  actions.className = 'caos-impersonate-ask__actions';
  const go = document.createElement('caos-button');
  go.setAttribute('variant', 'primary');
  go.setAttribute('size', 'sm');
  go.setAttribute('type', 'button');
  go.textContent = 'Act as this person';
  const cancel = document.createElement('caos-button');
  cancel.setAttribute('variant', 'ghost');
  cancel.setAttribute('size', 'sm');
  cancel.setAttribute('type', 'button');
  cancel.textContent = 'Cancel';
  actions.append(go, cancel);
  body.append(actions);
  overlay.append(body);

  const close = (): void => overlay.remove();
  cancel.addEventListener('click', close);
  overlay.addEventListener('caos-overlay-close', close);

  go.addEventListener('click', () => {
    const why = reason.value.trim();
    if (why === '') {
      // Said in words, next to the field, rather than by disabling the control: a disabled button
      // with no explanation is the same dead end the account menu already had.
      problem.textContent = 'Say why first — this is recorded against everything you do while acting as them.';
      problem.hidden = false;
      reason.focus();
      return;
    }
    go.toggleAttribute('disabled', true);
    void (async () => {
      try {
        // `reason` is spelled the way the route reads it and is set last, so a caller cannot
        // shadow it from `body` — the route requires exactly this name.
        const payload: Record<string, unknown> = { ...ask.body, reason: why };
        if (ask.offerWrite && writeBox?.checked === true) payload[ask.offerWrite.param] = true;
        // THE ROUTE IS THIS MODULE'S, NOT A CALLER'S. An earlier version took an `endpoint` off the
        // ask so an authored action could name its own, and a review showed what that bought: this
        // door reloads the whole document on any `{ok:true}` and reports nothing, because what it
        // posts to is supposed to have handed back a session. Aimed anywhere else it is a control
        // that performs an unexplained write and then blanks the screen. One route, named once, and
        // no caller can change it — which is also why `ImpersonationAsk` carries no endpoint field
        // for one to be passed in.
        const res = await doorFetch(IMPERSONATION_START_ENDPOINT, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const answer = (await res.json()) as { ok?: boolean; error?: { message?: string } } | null;
        if (answer?.ok !== true) {
          // The kernel's own refusal, in its own words — it knows why (a machine identity, somebody
          // holding a floor permission, an account that is not active, a write without
          // `users.impersonate_write`) and this surface does not.
          problem.textContent = answer?.error?.message ?? 'That could not be started just now.';
          problem.hidden = false;
          go.toggleAttribute('disabled', false);
          return;
        }
      } catch {
        problem.textContent = 'That could not be started — the server could not be reached.';
        problem.hidden = false;
        go.toggleAttribute('disabled', false);
        return;
      }
      // The host has set the impersonation cookie by now, so a full load comes back as them.
      doorLeaveTo('/');
    })();
  });

  document.body.append(overlay);
  reason.focus();
}
